import type { FunnelBucket } from "../../types/dealFunnel";
import type { SponsorTypeSummaryRow } from "../../utils/sponsorSummaryHelpers";
import {
  COLORS,
  FONTS,
  SLIDE_W,
  TABLE_ROW_BORDER,
  TITLE_H,
  addShape,
  addTableHeaderRule,
  addTitleBar,
  formatGbp,
  formatGbpMm,
  formatPercent,
  getShapes,
  type PptxInstance,
  type PptxSlide,
} from "./pptxCore";
import {
  chartColorsForCount,
  summaryTableCell,
} from "./summaryTableHelpers";

export type OriginationChannel = {
  key: "JLL" | "Other" | "Apollo" | "Direct";
  label: string;
  takeaway: string;
  deal_count: number;
  loan_amount_gbp: number;
  percentOfTotal: number;
};

export type MarketSlideInput = {
  channels: OriginationChannel[];
  channelTotalVolume: number;
  sponsors: SponsorTypeSummaryRow[];
  sponsorTotalVolume: number;
};

const CHANNEL_ORDER: OriginationChannel["key"][] = [
  "JLL",
  "Other",
  "Apollo",
  "Direct",
];

const CHANNEL_TAKEAWAYS: Record<OriginationChannel["key"], string> = {
  JLL: "Highest quality and consistency",
  Other: "Selective relationship-driven flow",
  Apollo: "Institutional co-origination",
  Direct: "Growing with platform visibility",
};

/** Exact case-insensitive source_name → channel key. */
export function mapSourceNameToChannel(
  name: string | null | undefined,
): OriginationChannel["key"] {
  const key = (name ?? "").trim().toLowerCase();
  if (key === "jll") return "JLL";
  if (key === "apollo") return "Apollo";
  if (key === "tenfifty") return "Direct";
  return "Other";
}

export function buildOriginationChannels(
  buckets: FunnelBucket[],
): { channels: OriginationChannel[]; totalVolume: number; totalDeals: number } {
  const agg = new Map<
    OriginationChannel["key"],
    { deal_count: number; loan_amount_gbp: number }
  >();
  for (const key of CHANNEL_ORDER) {
    agg.set(key, { deal_count: 0, loan_amount_gbp: 0 });
  }

  for (const bucket of buckets) {
    const channel = mapSourceNameToChannel(bucket.name);
    // Bucket totals, not a funnel stage: the caller filters by status, and deals in
    // closing have never reached the closed milestone.
    const prev = agg.get(channel)!;
    prev.deal_count += bucket.deal_count ?? 0;
    prev.loan_amount_gbp += bucket.loan_amount_gbp ?? 0;
  }

  const totalVolume = [...agg.values()].reduce(
    (sum, row) => sum + row.loan_amount_gbp,
    0,
  );
  const totalDeals = [...agg.values()].reduce(
    (sum, row) => sum + row.deal_count,
    0,
  );

  const channels: OriginationChannel[] = CHANNEL_ORDER.map((key) => {
    const row = agg.get(key)!;
    return {
      key,
      label: key === "Other" ? "Other advisors" : key === "Direct" ? "Direct" : key,
      takeaway: CHANNEL_TAKEAWAYS[key],
      deal_count: row.deal_count,
      loan_amount_gbp: row.loan_amount_gbp,
      percentOfTotal:
        totalVolume > 0 ? (row.loan_amount_gbp / totalVolume) * 100 : 0,
    };
  }).filter((row) => row.deal_count > 0 || row.loan_amount_gbp > 0);

  // Keep empty channels visible if everything is zero so the slide still reads.
  const display =
    channels.length > 0
      ? channels
      : CHANNEL_ORDER.map((key) => ({
          key,
          label:
            key === "Other"
              ? "Other advisors"
              : key === "Direct"
                ? "Direct"
                : key,
          takeaway: CHANNEL_TAKEAWAYS[key],
          deal_count: 0,
          loan_amount_gbp: 0,
          percentOfTotal: 0,
        }));

  return { channels: display, totalVolume, totalDeals };
}

function buildChannelTable(channels: OriginationChannel[]) {
  const header = [
    summaryTableCell("Volume", { bold: true, fill: COLORS.white }),
    summaryTableCell("Channel", { bold: true, fill: COLORS.white }),
    summaryTableCell("Key takeaway", { bold: true, fill: COLORS.white }),
  ];
  const body = channels.map((row) => [
    summaryTableCell(formatPercent(row.percentOfTotal), { bold: true }),
    summaryTableCell(row.label),
    summaryTableCell(row.takeaway),
  ]);
  return [header, ...body];
}

function buildSponsorTable(
  sponsors: SponsorTypeSummaryRow[],
  totalVolume: number,
) {
  const header = [
    summaryTableCell("Volume", { bold: true, fill: COLORS.white }),
    summaryTableCell("Sponsor type", { bold: true, fill: COLORS.white }),
    summaryTableCell("Deals", {
      bold: true,
      align: "right",
      fill: COLORS.white,
    }),
    summaryTableCell("Amount", {
      bold: true,
      align: "right",
      fill: COLORS.white,
    }),
  ];
  const body = sponsors.map((row) => {
    const pct = totalVolume > 0 ? (row.loan_amount_gbp / totalVolume) * 100 : 0;
    return [
      summaryTableCell(formatPercent(pct), { bold: true }),
      summaryTableCell(row.name),
      summaryTableCell(row.deal_count.toLocaleString("en-GB"), {
        align: "right",
      }),
      summaryTableCell(formatGbp(row.loan_amount_gbp), { align: "right" }),
    ];
  });
  return [header, ...body];
}

function largestShareLabel(
  rows: Array<{ name: string; percent: number }>,
): { name: string; percent: number } | null {
  if (rows.length === 0) return null;
  return [...rows].sort((a, b) => b.percent - a.percent)[0] ?? null;
}

function addTakeawayBox(
  pptx: PptxInstance,
  slide: PptxSlide,
  x: number,
  y: number,
  w: number,
  h: number,
  text?: string,
): void {
  addShape(slide, getShapes(pptx).ROUNDED_RECTANGLE, {
    x,
    y,
    w,
    h,
    fill: { color: COLORS.navy },
    line: { color: COLORS.navy, width: 0 },
    rectRadius: 0.1,
  });
  if (!text) return;
  slide.addText(
    [
      {
        text: "Key Takeaway:",
        options: { bold: true, breakLine: true, fontSize: 12 },
      },
      { text: text, options: { fontSize: 11 } },
    ],
    {
      x: x + 0.14,
      y: y + 0.14,
      w: w - 0.28,
      h: h - 0.28,
      fontFace: FONTS.body,
      color: COLORS.white,
      align: "left",
      valign: "middle",
      margin: 0,
    },
  );
}

function addMarketSection(
  pptx: PptxInstance,
  slide: PptxSlide,
  opts: {
    title: string;
    y: number;
    height: number;
    labels: string[];
    values: number[];
    totalVolume: number;
    tableRows: ReturnType<typeof buildChannelTable>;
    colW: number[];
    takeaway?: string;
  },
): void {
  const padX = 0.28;
  slide.addText(opts.title, {
    x: padX,
    y: opts.y,
    w: 6,
    h: 0.28,
    fontFace: FONTS.body,
    fontSize: 14,
    bold: true,
    color: COLORS.textDark,
    margin: 0,
  });

  const contentY = opts.y + 0.3;
  const contentH = opts.height - 0.3;
  const chartW = 3.5;
  const tableW = 5.6;
  const takeawayW = 3.4;
  const gap = 0.2;
  const chartX = padX;
  const tableX = chartX + chartW + gap;
  const takeawayX = tableX + tableW + gap;

  if (opts.values.some((v) => v > 0)) {
    slide.addChart(pptx.ChartType.doughnut, [
      {
        name: opts.title,
        labels: opts.labels,
        values: opts.values,
      },
    ], {
      x: chartX,
      y: contentY,
      w: chartW,
      h: contentH,
      showTitle: false,
      showPercent: true,
      showLegend: false,
      chartColors: chartColorsForCount(opts.labels.length),
      holeSize: 55,
      dataLabelFontFace: FONTS.body,
      dataLabelFontSize: 8,
      dataLabelFontBold: true,
      dataLabelColor: COLORS.white,
    });
    slide.addText(
      [
        {
          text: formatGbpMm(opts.totalVolume),
          options: { bold: true, fontSize: 11, breakLine: true },
        },
        { text: "FF Volume", options: { bold: false, fontSize: 9 } },
      ],
      {
        x: chartX + 0.55,
        y: contentY + contentH / 2 - 0.25,
        w: chartW - 1.1,
        h: 0.5,
        fontFace: FONTS.body,
        color: COLORS.textDark,
        align: "center",
        valign: "middle",
        margin: 0,
      },
    );
  }

  const tableRowH = Math.min(0.28, contentH / opts.tableRows.length);
  const tableH = tableRowH * opts.tableRows.length;

  slide.addTable(opts.tableRows, {
    x: tableX,
    y: contentY,
    w: tableW,
    h: tableH,
    colW: opts.colW,
    rowH: tableRowH,
    border: TABLE_ROW_BORDER,
    fontFace: FONTS.body,
    fontSize: 8,
    color: COLORS.textDark,
    valign: "middle",
  });

  addTableHeaderRule(pptx, slide, {
    x: tableX,
    y: contentY,
    w: tableW,
    headerRowH: tableRowH,
  });

  addTakeawayBox(
    pptx,
    slide,
    takeawayX,
    contentY,
    Math.min(takeawayW, SLIDE_W - takeawayX - 0.28),
    contentH,
    opts.takeaway,
  );
}

export function addMarketOriginationSlide(
  pptx: PptxInstance,
  input: MarketSlideInput,
  logoDataUri?: string | null,
): PptxSlide {
  const slide = pptx.addSlide();
  slide.background = { color: COLORS.white };

  addTitleBar(pptx, slide, {
    title: "Market – Origination & Sponsor Ecosystem",
    logoDataUri,
  });

  const totalDeals = input.channels.reduce((sum, c) => sum + c.deal_count, 0);
  const subtitle = `${totalDeals.toLocaleString("en-GB")} deals representing ${formatGbpMm(input.channelTotalVolume)} fully funded commitments sourced through institutional advisory relationships, strategic networks and growing direct origination.`;

  slide.addText(subtitle, {
    x: 0.28,
    y: TITLE_H + 0.08,
    w: SLIDE_W - 0.56,
    h: 0.42,
    fontFace: FONTS.body,
    fontSize: 11,
    color: COLORS.muted,
    align: "left",
    valign: "middle",
    margin: 0,
  });

  const topChannel = largestShareLabel(
    input.channels.map((c) => ({
      name: c.label,
      percent: c.percentOfTotal,
    })),
  );
  const channelTakeaway = topChannel
    ? `Origination is led by ${topChannel.name} (~${Math.round(topChannel.percent)}% of closed volume), with institutional relationships and growing direct sourcing.`
    : "Origination is increasingly driven by institutional relationships, repeat counterparties and growing direct sourcing.";

  const bodyTop = TITLE_H + 0.55;
  const sectionH = 2.95;
  const sectionGap = 0.2;

  addMarketSection(pptx, slide, {
    title: "Origination channels",
    y: bodyTop,
    height: sectionH,
    labels: input.channels.map((c) => c.label),
    values: input.channels.map((c) => c.loan_amount_gbp),
    totalVolume: input.channelTotalVolume,
    tableRows: buildChannelTable(input.channels),
    colW: [0.9, 1.5, 3.2],
    takeaway: channelTakeaway,
  });

  const sponsorsSorted = [...input.sponsors].sort(
    (a, b) => b.loan_amount_gbp - a.loan_amount_gbp,
  );
  addMarketSection(pptx, slide, {
    title: "Sponsor Base by Volume",
    y: bodyTop + sectionH + sectionGap,
    height: sectionH,
    labels: sponsorsSorted.map((s) => s.name),
    values: sponsorsSorted.map((s) => s.loan_amount_gbp),
    totalVolume: input.sponsorTotalVolume,
    tableRows: buildSponsorTable(sponsorsSorted, input.sponsorTotalVolume),
    colW: [0.85, 2.3, 0.85, 1.6],
  });

  return slide;
}
